import api from '@/api/api.js'
import dayjs from 'dayjs'
import { DataLoadState } from '@/utils/storeUtils.js'
import {
  eventManager,
  GPS_OWNER_CHANGED_EVENT,
  GPS_SUBSCRIPTION_CHANGED_EVENT
} from '@/eventManager.js'

// Mutation names
const SET_IMEI = 'setImei'
const SET_GPS_LOAD_STARTED = 'setGPSLoadStarted'
const SET_GPS_LOAD_FINISHED_SUCCESSFULLY = 'setGPSLoadFinishedSuccessfully'
const SET_GPS_LOAD_FAILED = 'setGPSLoadFailed'
const RESET_GPS = 'resetGPS'
const RESET_GPS_DEVICE = 'resetGPSDevices'

const SET_HISTORY_DATES_LOAD_STARTED = 'setHistoryDatesLoadStarted'
const SET_HISTORY_DATES_LOAD_FINISHED_SUCCESSFULLY = 'setHistoryDatesLoadFinishedSuccessfully'
const SET_HISTORY_DATES_LOAD_FAILED = 'setHistoryDatesLoadFailed'

const SET_GPS_POSITIONS_LOAD_STARTED = 'setGPSPositionsLoadStarted'
const SET_GPS_POSITIONS_LOAD_FINISHED_SUCCESSFULLY = 'setGPSPositionsLoadFinishedSuccessfully'
const SET_GPS_POSITIONS_LOAD_FAILED = 'setGPSPositionsLoadLoadFailed'

const SET_WARRANTY_INFO_LOAD_STARTED = 'setWarrantyInfoLoadStarted'
const SET_WARRANTY_INFO_LOAD_FINISHED_SUCCESSFULLY = 'setWarrantyInfoLoadFinishedSuccessfully'
const SET_WARRANTY_INFO_LOAD_FAILED = 'setWarrantyInfoLoadLoadFailed'

const SET_DETAILS_LOAD_STARTED = 'setDetailsLoadStarted'
const SET_DETAILS_LOAD_FINISHED_SUCCESSFULLY = 'setDetailsLoadFinishedSuccessfully'
const SET_DETAILS_LOAD_FAILED = 'setDetailsLoadFailed'

const SET_GPS_DEVICES_LOAD_STARTED = 'setGPSDevicesLoadStarted'
const SET_GPS_DEVICES_LOAD_FINISHED_SUCCESSFULLY = 'setGPSDevicesLoadFinishedSuccessfully'
const SET_GPS_DEVICES_LOAD_FAILED = 'setGPSDevicesLoadFailed'

// Getter names
export const GET_IMEI = 'getImei'
export const IS_LOADING_GPS = 'isLoadingGPS'
export const GET_GPS = 'getGPS'
export const GET_GPS_LOAD_ERROR = 'getGPSLoadError'

export const IS_LOADING_HISTORY_DATES = 'isLoadingHistoryDates'
export const GET_HISTORY_DATES = 'getHistoryDates'
export const GET_HISTORY_DATES_LOAD_ERROR = 'getHistoryDatesLoadError'
export const HAS_EMPTY_HISTORY_DATES_LOAD_RESULT = 'hasEmptyHistoryDatesLoadResult'

export const IS_LOADING_GPS_POSITIONS = 'isLoadingGPSPositions'
export const GET_GPS_POSITIONS = 'getGPSPositions'
export const GET_GPS_POSITIONS_LOAD_ERROR = 'getGPSPositionsLoadError'
export const HAS_EMPTY_GPS_POSITIONS_LOAD_RESULT = 'hasEmptyGPSPositionsLoadResult'

export const IS_LOADING_WARRANTY_INFO = 'isLoadingWarrantyInfo'
export const GET_WARRANTY_INFO = 'getWarrantyInfo'
export const GET_WARRANTY_INFO_LOAD_ERROR = 'getWarrantyInfoLoadError'

export const IS_LOADING_DETAILS = 'isLoadingDetails'
export const GET_DETAILS = 'getDetails'
export const GET_DETAILS_LOAD_ERROR = 'getDetailsLoadError'

export const IS_LOADING_GPS_DEVICES = 'isLoadingGPSDevices'
export const GET_GPS_DEVICES = 'getGPSDevices'
export const GET_GPS_DEVICES_LOAD_ERROR = 'getGPSDevicesLoadError'

// Action name
export const SET_WEHUNT_GPS = 'setWeHuntGPS'
export const LOAD_GPS_WITH_IMEI = 'loadGPSWithIMEI'
export const LOAD_HISTORY_DATES = 'loadHistoryDates'
export const LOAD_GPS_POSITIONS_FOR_DATE = 'loadGPSPositionsForDate'
export const CHANGE_GPS_OWNER = 'changeGPSOwner'
export const TRANSFER_GPS_SUBSCRIPTION = 'transferGPSSubscription'
export const SET_GPS_SUBSCRIPTION_EXPIRATION = 'setGPSSubscriptionExpiration'
export const LOAD_GPS_WARRANTY_INFO = 'loadWarrantyInfo'
export const LOAD_GPS_DETAILS = 'loadDetails'
export const LOAD_GPS_DEVICES = 'loadGPSDevices'
export const RELOAD_GPS_DEVICES = 'reloadGPSDevices'

// GPS store implementation
export const WeHuntGPSStore = {
  namespaced: true,

  state: () => ({
    // state must be a function to allow reuse of this store
    imei: null,
    gpsState: new DataLoadState(),
    historyDatesState: new DataLoadState(),
    gpsPositionsState: new DataLoadState(),
    warrantyInfoState: new DataLoadState(),
    detailsState: new DataLoadState(),
    gpsDevicesState: new DataLoadState()
  }),

  mutations: {
    [SET_IMEI](state, { imei }) {
      state.imei = imei
    },

    [SET_WEHUNT_GPS](state, { gps }) {
      if (!gps || !gps.imei) {
        state.gpsState = new DataLoadState()
        state.historyDatesState = new DataLoadState()
        state.gpsPositionsState = new DataLoadState()
        state.warrantyInfoState = new DataLoadState()
        state.gpsDevicesState = new DataLoadState()
        return
      }

      state.gpsState = new DataLoadState(gps.imei).stateForLoadFinished(gps)
    },

    [SET_GPS_LOAD_STARTED](state, { imei }) {
      const dataLoadState = new DataLoadState(imei)
      state.gpsState = dataLoadState.stateForLoadStarted()
    },

    [SET_GPS_LOAD_FINISHED_SUCCESSFULLY](state, { gps }) {
      if (gps.imei !== state.gpsState.id) {
        // GPS load is stale, ignore
        return
      }

      state.gpsState = state.gpsState.stateForLoadFinished(gps)
    },

    [SET_GPS_LOAD_FAILED](state, { imei, error }) {
      if (imei !== state.gpsState.id) {
        // GPS load is stale, ignore
        return
      }

      state.gpsState = state.gpsState.stateForLoadFailed(error)
    },

    [RESET_GPS](state, { imei }) {
      if (imei !== state.gpsState.id) {
        // GPS has changed, ignore
        return
      }

      state.gpsState = new DataLoadState()
    },

    [RESET_GPS_DEVICE](state, { imei }) {
      if (imei !== state.gpsDevicesState.id) {
        // GPS devices has changed, ignore
        return
      }

      state.gpsDevicesState = new DataLoadState()
    },

    [SET_HISTORY_DATES_LOAD_STARTED](state, { imei }) {
      const dataLoadState = new DataLoadState(imei)
      state.historyDatesState = dataLoadState.stateForLoadStarted()
    },

    [SET_HISTORY_DATES_LOAD_FINISHED_SUCCESSFULLY](state, { imei, historyDates }) {
      if (imei !== state.historyDatesState.id) {
        // History dates load is stale, ignore
        return
      }

      state.historyDatesState = state.historyDatesState.stateForLoadFinished(historyDates)
    },

    [SET_HISTORY_DATES_LOAD_FAILED](state, { imei, error }) {
      if (imei !== state.historyDatesState.id) {
        // History dates load is stale, ignore
        return
      }

      state.historyDatesState = state.historyDatesState.stateForLoadFailed(error)
    },

    [SET_GPS_POSITIONS_LOAD_STARTED](state, { dateString }) {
      const dataLoadState = new DataLoadState(dateString)
      state.gpsPositionsState = dataLoadState.stateForLoadStarted()
    },

    [SET_GPS_POSITIONS_LOAD_FINISHED_SUCCESSFULLY](state, { dateString, gpsPositions }) {
      if (dateString !== state.gpsPositionsState.id) {
        // GPS positions load is stale, ignore
        return
      }

      state.gpsPositionsState = state.gpsPositionsState.stateForLoadFinished(gpsPositions)
    },

    [SET_GPS_POSITIONS_LOAD_FAILED](state, { dateString, error }) {
      if (dateString !== state.gpsPositionsState.id) {
        // GPS positions load is stale, ignore
        return
      }

      state.gpsPositionsState = state.gpsPositionsState.stateForLoadFailed(error)
    },

    [SET_WARRANTY_INFO_LOAD_STARTED](state, { imei }) {
      const dataLoadState = new DataLoadState(imei)
      state.warrantyInfoState = dataLoadState.stateForLoadStarted()
    },

    [SET_WARRANTY_INFO_LOAD_FINISHED_SUCCESSFULLY](state, { imei, warrantyInfo }) {
      if (imei !== state.warrantyInfoState.id) {
        // Warranty info load is stale, ignore
        return
      }

      state.warrantyInfoState = state.warrantyInfoState.stateForLoadFinished(warrantyInfo)
    },

    [SET_WARRANTY_INFO_LOAD_FAILED](state, { imei, error }) {
      if (imei !== state.warrantyInfoState.id) {
        // Warranty info load is stale, ignore
        return
      }

      state.warrantyInfoState = state.warrantyInfoState.stateForLoadFailed(error)
    },

    [SET_DETAILS_LOAD_STARTED](state, { imei }) {
      const dataLoadState = new DataLoadState(imei)
      state.detailsState = dataLoadState.stateForLoadStarted()
    },

    [SET_DETAILS_LOAD_FINISHED_SUCCESSFULLY](state, { imei, details }) {
      if (imei !== state.detailsState.id) {
        // Details load is stale, ignore
        return
      }

      state.detailsState = state.detailsState.stateForLoadFinished(details)
    },

    [SET_DETAILS_LOAD_FAILED](state, { imei, error }) {
      if (imei !== state.detailsState.id) {
        // Details load is stale, ignore
        return
      }

      state.detailsState = state.detailsState.stateForLoadFailed(error)
    },

    [SET_GPS_DEVICES_LOAD_STARTED](state, { imei }) {
      const dataLoadState = new DataLoadState(imei)
      state.gpsDevicesState = dataLoadState.stateForLoadStarted()
    },

    [SET_GPS_DEVICES_LOAD_FINISHED_SUCCESSFULLY](state, { imei, gpsDevices }) {
      if (imei !== state.gpsDevicesState.id) {
        // GPS devices load is stale, ignore
        return
      }

      state.gpsDevicesState = state.gpsDevicesState.stateForLoadFinished(gpsDevices)
    },

    [SET_GPS_DEVICES_LOAD_FAILED](state, { imei, error }) {
      if (imei !== state.gpsDevicesState.id) {
        // GPS devices load is stale, ignore
        return
      }

      state.gpsDevicesState = state.gpsDevicesState.stateForLoadFailed(error)
    }
  },

  getters: {
    [GET_IMEI]: state => {
      return state.imei
    },

    [IS_LOADING_GPS]: state => {
      return state.gpsState.status.loading
    },

    [GET_GPS]: state => {
      return state.gpsState.data
    },

    [GET_GPS_LOAD_ERROR]: state => {
      return state.gpsState.status.error
    },

    [IS_LOADING_HISTORY_DATES]: state => {
      return state.historyDatesState.status.loading
    },

    [GET_HISTORY_DATES]: state => {
      return state.historyDatesState.data
    },

    [GET_HISTORY_DATES_LOAD_ERROR]: state => {
      return state.historyDatesState.status.error
    },

    [HAS_EMPTY_HISTORY_DATES_LOAD_RESULT]: state => {
      return state.historyDatesState.status.success && state.historyDatesState.data.length === 0
    },

    [IS_LOADING_GPS_POSITIONS]: state => {
      return state.gpsPositionsState.status.loading
    },

    [GET_GPS_POSITIONS]: state => {
      return state.gpsPositionsState.data
    },

    [GET_GPS_POSITIONS_LOAD_ERROR]: state => {
      return state.gpsPositionsState.status.error
    },

    [HAS_EMPTY_GPS_POSITIONS_LOAD_RESULT]: state => {
      return state.gpsPositionsState.status.success && state.gpsPositionsState.data.length === 0
    },

    [IS_LOADING_WARRANTY_INFO]: state => {
      return state.warrantyInfoState.status.loading
    },

    [GET_WARRANTY_INFO]: state => {
      return state.warrantyInfoState.data
    },

    [GET_WARRANTY_INFO_LOAD_ERROR]: state => {
      return state.warrantyInfoState.status.error
    },

    [IS_LOADING_DETAILS]: state => {
      return state.detailsState.status.loading
    },

    [GET_DETAILS]: state => {
      return state.detailsState.data
    },

    [GET_DETAILS_LOAD_ERROR]: state => {
      return state.detailsState.status.error
    },

    [IS_LOADING_GPS_DEVICES]: state => {
      return state.gpsDevicesState.status.loading
    },

    [GET_GPS_DEVICES]: state => {
      return state.gpsDevicesState.data
    },

    [GET_GPS_DEVICES_LOAD_ERROR]: state => {
      return state.gpsDevicesState.status.error
    }
  },

  actions: {
    [LOAD_GPS_WITH_IMEI]: async ({ commit, state }, { imei }) => {
      if (!imei) {
        return
      }

      if (imei === state.gpsState.id) {
        // GPS load has already started
        return
      }

      commit(SET_IMEI, { imei })
      commit(SET_GPS_LOAD_STARTED, { imei })

      try {
        const response = await api.get(`admin/wehuntgps/${imei}`)
        const gps = response.data

        commit(SET_GPS_LOAD_FINISHED_SUCCESSFULLY, { gps })
      } catch (error) {
        commit(SET_GPS_LOAD_FAILED, { imei, error })
      }
    },

    [SET_WEHUNT_GPS]: ({ commit }, { gps }) => {
      var imei = null
      if (gps) {
        imei = gps.imei || null
      }

      commit(SET_IMEI, { imei })
      commit(SET_WEHUNT_GPS, { gps })
    },

    [LOAD_HISTORY_DATES]: async ({ commit, state }) => {
      const imei = state.imei
      if (!imei) {
        // No imei
        return
      }

      if (imei === state.historyDatesState.id) {
        // History dates load has already started
        return
      }

      commit(SET_HISTORY_DATES_LOAD_STARTED, { imei })

      try {
        const response = await api.get(`admin/wehuntgps/${imei}/historyDates`)

        const historyDates = response.data
        commit(SET_HISTORY_DATES_LOAD_FINISHED_SUCCESSFULLY, { imei, historyDates })
      } catch (error) {
        commit(SET_HISTORY_DATES_LOAD_FAILED, { imei, error })
      }
    },

    [LOAD_GPS_POSITIONS_FOR_DATE]: async ({ commit, state }, { date }) => {
      if (!date) {
        return
      }

      const imei = state.imei
      if (!imei) {
        // No imei
        return
      }

      const dateString = dayjs(date).format('YYYY-MM-DD')
      if (dateString === state.gpsPositionsState.id) {
        // GPS positions load has already started
        return
      }

      commit(SET_GPS_POSITIONS_LOAD_STARTED, { dateString })

      try {
        const response = await api.get(`admin/wehuntgps/${imei}/history/${dateString}`)

        const gpsPositions = response.data
        commit(SET_GPS_POSITIONS_LOAD_FINISHED_SUCCESSFULLY, { dateString, gpsPositions })
      } catch (error) {
        commit(SET_GPS_POSITIONS_LOAD_FAILED, { dateString, error })
      }
    },

    [CHANGE_GPS_OWNER]: async ({ commit, dispatch }, { imei, oldOwnerEmail, newOwnerEmail }) => {
      return api
        .post(`admin/wehuntgps/${imei}/transferOwner`, { imei, oldOwnerEmail, newOwnerEmail })
        .then(response => {
          if (!response.data.success) {
            return response.data
          }

          // Reload GPS data after owner has been updated
          commit(RESET_GPS, { imei })
          dispatch(LOAD_GPS_WITH_IMEI, { imei })

          // Emit event to allow other stores to reload or delete remove stale data
          eventManager.$emit(GPS_OWNER_CHANGED_EVENT, { imei, oldOwnerEmail, newOwnerEmail })

          return response.data
        })
    },

    [TRANSFER_GPS_SUBSCRIPTION]: async ({ commit }, { imei, email, newImei }) => {
      return api
        .post(`admin/wehuntgps/${imei}/transferSubscription`, { email, newImei })
        .then(response => {
          if (!response.data.success) {
            return response.data
          }

          // Reset GPS data after IMEI has been updated
          commit(RESET_GPS, { imei })

          // Emit event to allow other stores to reload or delete remove stale data
          eventManager.$emit(GPS_SUBSCRIPTION_CHANGED_EVENT, { email, imei, newImei })

          return response.data
        })
    },

    [SET_GPS_SUBSCRIPTION_EXPIRATION]: async (
      { state, commit, dispatch },
      { email, newGPSSubscriptionExpirationDate }
    ) => {
      if (!newGPSSubscriptionExpirationDate) {
        return
      }

      const imei = state.imei
      if (!imei) {
        // No imei
        return
      }

      const expirationDate = dayjs(newGPSSubscriptionExpirationDate)
        .utc()
        .toISOString()

      return api
        .post(`admin/wehuntgps/${imei}/setSubscription`, { email, expirationDate })
        .then(response => {
          if (!response.data.success) {
            return response.data
          }

          // Reset GPS data after IMEI has been updated
          commit(RESET_GPS, { imei })
          dispatch(LOAD_GPS_WITH_IMEI, { imei })

          // Emit event to allow other stores to reload or delete remove stale data
          eventManager.$emit(GPS_SUBSCRIPTION_CHANGED_EVENT, { email, imei })

          return response.data
        })
    },

    [LOAD_GPS_WARRANTY_INFO]: async ({ commit, state }) => {
      const imei = state.imei
      if (!imei) {
        // No imei
        return
      }

      if (imei === state.warrantyInfoState.id) {
        // Warranty info load has already started
        return
      }

      commit(SET_WARRANTY_INFO_LOAD_STARTED, { imei })

      try {
        const response = await api.get(`admin/wehuntgps/${imei}/warranty`)
        const loadResult = response.data

        if (!loadResult.success || !loadResult.info) {
          const error = loadResult.message || 'Unknown error'
          commit(SET_WARRANTY_INFO_LOAD_FAILED, { imei, error })
        } else {
          commit(SET_WARRANTY_INFO_LOAD_FINISHED_SUCCESSFULLY, {
            imei,
            warrantyInfo: loadResult.info
          })
        }
      } catch (error) {
        commit(SET_WARRANTY_INFO_LOAD_FAILED, { imei, error })
      }
    },

    [LOAD_GPS_DETAILS]: async ({ commit, state }) => {
      const imei = state.imei
      if (!imei) {
        // No imei
        return
      }

      if (imei === state.detailsState.id) {
        // Details load has already started
        return
      }

      commit(SET_DETAILS_LOAD_STARTED, { imei })

      try {
        const response = await api.get(`admin/wehuntgps/${imei}/details`)

        const details = response.data
        commit(SET_DETAILS_LOAD_FINISHED_SUCCESSFULLY, { imei, details })
      } catch (error) {
        commit(SET_DETAILS_LOAD_FAILED, { imei, error })
      }
    },

    [LOAD_GPS_DEVICES]: async ({ commit, state }) => {
      const imei = state.imei
      if (!imei) {
        // No imei
        return
      }

      if (imei === state.gpsDevicesState.id) {
        // GPS devices load has already started
        return
      }

      commit(SET_GPS_DEVICES_LOAD_STARTED, { imei })

      try {
        const response = await api.get(`admin/wehuntgps/${imei}/gpsDevices`)

        const gpsDevices = response.data
        commit(SET_GPS_DEVICES_LOAD_FINISHED_SUCCESSFULLY, { imei, gpsDevices })
      } catch (error) {
        commit(SET_GPS_DEVICES_LOAD_FAILED, { imei, error })
      }
    },

    [RELOAD_GPS_DEVICES]: ({ commit, state, dispatch }) => {
      commit(RESET_GPS_DEVICE, { imei: state.imei })
      dispatch(LOAD_GPS_DEVICES, { imei: state.imei })
    }
  }
}
